import React from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Paper
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Security as SecurityIcon,
  IntegrationInstructions as IntegrationIcon,
  Speed as SpeedIcon,
  Support as SupportIcon,
  Assessment as AssessmentIcon,
  Storage as StorageIcon,
  Public as PublicIcon,
  Sync as SyncIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const UIPSystemDetail: React.FC = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate('/admin/pi-planning');
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mr: 2 }}
          aria-label="Back to PI Planning Board"
        >
          Back
        </Button>
        <Box>
          <Typography variant="h4" sx={{ color: '#003366', fontWeight: 'bold' }}>
            Unified Immigration Portal (UIP)
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            CBP Real-Time Immigration and Travel Records Integration Platform
          </Typography>
        </Box>
      </Box>

      {/* System Overview */}
      <Card sx={{ mb: 3, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            System Overview
          </Typography>
          <Typography variant="body1" paragraph>
            The Unified Immigration Portal (UIP) is a mission-critical, real-time integration platform that provides 
            U.S. Customs and Border Protection (CBP) officers with immediate access to comprehensive immigration and 
            travel records from multiple DHS systems. UIP consolidates data from USCIS, ICE, and other agencies to 
            support rapid, informed decision-making at ports of entry and border checkpoints.
          </Typography>
        </CardContent>
      </Card>

      {/* 5Ws Summary - Compact Table Format */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <AssessmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        System Overview: The 5Ws
      </Typography>

      <Card sx={{ mb: 4 }}>
        <TableContainer>
          <Table aria-label="UIP System 5Ws Overview">
            <TableBody>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ width: '15%', bgcolor: '#D32F2F', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="What is UIP"
                >
                  What
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    A <strong>real-time integration platform</strong> that aggregates immigration, travel, and enforcement 
                    data from multiple DHS systems into a unified interface for CBP officers. UIP provides <strong>single-query 
                    access</strong> to USCIS benefit records, ICE enforcement data, travel history, biometric information, 
                    and watchlist alerts, enabling rapid admissibility determinations at ports of entry.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#1976D2', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Who uses UIP"
                >
                  Who
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    Operated by <strong>CBP Office of Information and Technology (OIT)</strong> and used by CBP Officers 
                    (CBPOs) at air, land, and sea ports of entry. Primary users include <strong>primary and secondary 
                    inspection officers, supervisory CBPOs, and targeting specialists</strong>. System supports over 
                    <strong> 20,000 CBP officers</strong> conducting traveler inspections and admissibility determinations.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#2E7D32', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="When was UIP deployed"
                >
                  When
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    Deployed in <strong>2015</strong> as part of CBP's modernization initiative to replace fragmented 
                    legacy query systems. Enhanced with <strong>USCIS ELIS integration (2017)</strong>, mobile access 
                    capabilities (2018), and <strong>advanced analytics dashboard (2020)</strong>. Operates <strong>24/7/365</strong> 
                    supporting real-time border operations with sub-second query response times.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#F57C00', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Where is UIP deployed"
                >
                  Where
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    Deployed at <strong>all 328 CBP ports of entry</strong> including air, land, and sea ports nationwide. 
                    Accessible from <strong>primary inspection booths, secondary inspection areas, and mobile devices</strong> 
                    for roving patrols. Cloud-based architecture enables access from <strong>CBP preclearance locations 
                    in 15 foreign countries</strong> and remote Border Patrol checkpoints.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#7B1FA2', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Why UIP exists"
                >
                  Why
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    To provide <strong>rapid, comprehensive access</strong> to immigration and travel records during 
                    traveler inspections, eliminating the need for officers to query multiple systems separately. Enables 
                    <strong> informed admissibility decisions</strong> based on complete traveler history, reduces inspection 
                    times while enhancing security, and supports <strong>national security screening</strong> through 
                    integrated watchlist and threat information.
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Core Capabilities */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <IntegrationIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Core System Capabilities
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #1976D2' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <IntegrationIcon sx={{ fontSize: 40, color: '#1976D2', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                  USCIS System Integration
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Real-time connectivity to USCIS benefit and case management systems.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>ELIS Integration:</strong> Electronic Immigration System benefit applications</Typography></li>
                <li><Typography variant="body2"><strong>CIS Database:</strong> Central Index System for immigration records</Typography></li>
                <li><Typography variant="body2"><strong>CLAIMS:</strong> Computer Linked Application Information Management System</Typography></li>
                <li><Typography variant="body2"><strong>Benefit Status:</strong> Real-time visa, green card, and citizenship status</Typography></li>
                <li><Typography variant="body2"><strong>Petition History:</strong> Family and employment-based petition tracking</Typography></li>
                <li><Typography variant="body2"><strong>Naturalization Records:</strong> Citizenship application and ceremony data</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #D32F2F' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SecurityIcon sx={{ fontSize: 40, color: '#D32F2F', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#D32F2F', fontWeight: 'bold' }}>
                  ICE Data Connectivity
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Comprehensive access to enforcement and removal operations data.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>EID Integration:</strong> Enforcement Integrated Database case records</Typography></li>
                <li><Typography variant="body2"><strong>EARM Access:</strong> Enforcement Alien Removal Module data</Typography></li>
                <li><Typography variant="body2"><strong>Detention Records:</strong> Current and historical custody information</Typography></li>
                <li><Typography variant="body2"><strong>Removal Orders:</strong> Final orders of removal and deportation history</Typography></li>
                <li><Typography variant="body2"><strong>Bond Information:</strong> Immigration bond status and conditions</Typography></li>
                <li><Typography variant="body2"><strong>Court Proceedings:</strong> Immigration court hearing schedules and outcomes</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SpeedIcon sx={{ fontSize: 40, color: '#2E7D32', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                  Real-Time Record Access
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Sub-second query response times for critical operational decisions.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Single Query Interface:</strong> One search retrieves data from all systems</Typography></li>
                <li><Typography variant="body2"><strong>Biographic Search:</strong> Name, DOB, nationality, passport number queries</Typography></li>
                <li><Typography variant="body2"><strong>Biometric Matching:</strong> Fingerprint and photo verification integration</Typography></li>
                <li><Typography variant="body2"><strong>Travel History:</strong> Complete entry/exit records from ADIS</Typography></li>
                <li><Typography variant="body2"><strong>Watchlist Alerts:</strong> Real-time TSDB and law enforcement lookouts</Typography></li>
                <li><Typography variant="body2"><strong>Document Verification:</strong> Visa and passport authenticity checks</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #F57C00' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SupportIcon sx={{ fontSize: 40, color: '#F57C00', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                  Officer Decision Support
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Intelligent tools and analytics to support admissibility determinations.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Risk Indicators:</strong> Automated flags for potential inadmissibility issues</Typography></li>
                <li><Typography variant="body2"><strong>Decision Guidance:</strong> Context-sensitive help and policy references</Typography></li>
                <li><Typography variant="body2"><strong>Historical Patterns:</strong> Previous encounters and inspection outcomes</Typography></li>
                <li><Typography variant="body2"><strong>Alert Notifications:</strong> Real-time updates on status changes and new information</Typography></li>
                <li><Typography variant="body2"><strong>Supervisor Escalation:</strong> Built-in referral and consultation workflows</Typography></li>
                <li><Typography variant="body2"><strong>Analytics Dashboard:</strong> Trend analysis and operational intelligence</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* System Integration Architecture */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <StorageIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        System Integration Architecture
      </Typography>

      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#003366' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Source System</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Data Type</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Integration Method</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Response Time</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell><strong>USCIS ELIS</strong></TableCell>
              <TableCell>Benefit applications, petitions, status updates</TableCell>
              <TableCell>REST API / Real-time</TableCell>
              <TableCell>&lt;500ms</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>USCIS CIS</strong></TableCell>
              <TableCell>Central immigration records, A-numbers, case history</TableCell>
              <TableCell>Database query / Real-time</TableCell>
              <TableCell>&lt;300ms</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>ICE EID</strong></TableCell>
              <TableCell>Enforcement cases, removal orders, detention records</TableCell>
              <TableCell>Web services / Real-time</TableCell>
              <TableCell>&lt;400ms</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>TECS</strong></TableCell>
              <TableCell>Border crossing history, lookouts, law enforcement data</TableCell>
              <TableCell>Direct database / Real-time</TableCell>
              <TableCell>&lt;200ms</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>IDENT</strong></TableCell>
              <TableCell>Biometric records, fingerprints, photos</TableCell>
              <TableCell>Biometric API / Real-time</TableCell>
              <TableCell>&lt;600ms</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>ADIS</strong></TableCell>
              <TableCell>Arrival/departure records, I-94 data, overstays</TableCell>
              <TableCell>Database query / Real-time</TableCell>
              <TableCell>&lt;350ms</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>FBI TSDB</strong></TableCell>
              <TableCell>Terrorist screening, watchlist matches</TableCell>
              <TableCell>Secure API / Real-time</TableCell>
              <TableCell>&lt;500ms</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>State Department CCD</strong></TableCell>
              <TableCell>Visa issuance, passport data, consular records</TableCell>
              <TableCell>Web services / Near real-time</TableCell>
              <TableCell>&lt;800ms</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* System Performance Metrics */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <PublicIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        System Performance & Scale
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #1976D2' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                20K+
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active Users
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                CBP Officers nationwide
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                15M+
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Daily Queries
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Peak: 25M during holidays
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #F57C00' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                &lt;1s
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Average Response Time
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                99.9% queries under 2 seconds
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #D32F2F' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#D32F2F', fontWeight: 'bold' }}>
                99.95%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                System Uptime
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                24/7/365 availability
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Key Benefits */}
      <Card sx={{ mb: 4, bgcolor: '#e3f2fd', border: '2px solid #1976D2' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1976D2' }}>
            Operational Benefits
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Reduced Query Time:</strong> 75% reduction vs. legacy multi-system queries</Typography></li>
                <li><Typography variant="body2"><strong>Enhanced Security:</strong> Comprehensive screening with integrated watchlists</Typography></li>
                <li><Typography variant="body2"><strong>Faster Inspections:</strong> Average 30% reduction in secondary inspection time</Typography></li>
                <li><Typography variant="body2"><strong>Improved Accuracy:</strong> Single source of truth reduces data inconsistencies</Typography></li>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Officer Efficiency:</strong> Simplified workflow increases throughput</Typography></li>
                <li><Typography variant="body2"><strong>Better Decisions:</strong> Complete information enables informed admissibility calls</Typography></li>
                <li><Typography variant="body2"><strong>Traveler Experience:</strong> Faster processing reduces wait times</Typography></li>
                <li><Typography variant="body2"><strong>Cost Savings:</strong> Reduced training and system maintenance costs</Typography></li>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Technical Features */}
      <Card sx={{ mb: 4, bgcolor: '#fff3e0', border: '2px solid #F57C00' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#F57C00' }}>
            Technical Features & Architecture
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Platform Architecture:
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">Cloud-based microservices architecture</Typography></li>
                <li><Typography variant="body2">Kubernetes container orchestration</Typography></li>
                <li><Typography variant="body2">Load-balanced API gateway</Typography></li>
                <li><Typography variant="body2">Redis caching for performance</Typography></li>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Security & Compliance:
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">End-to-end encryption (TLS 1.3)</Typography></li>
                <li><Typography variant="body2">Multi-factor authentication (PIV/CAC)</Typography></li>
                <li><Typography variant="body2">Role-based access control (RBAC)</Typography></li>
                <li><Typography variant="body2">Comprehensive audit logging</Typography></li>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                User Interface:
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">Responsive web application</Typography></li>
                <li><Typography variant="body2">Mobile device support (tablets)</Typography></li>
                <li><Typography variant="body2">Section 508 accessibility compliance</Typography></li>
                <li><Typography variant="body2">Multilingual support (English/Spanish)</Typography></li>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Integration Ecosystem */}
      <Card sx={{ mb: 4, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            <SyncIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            DHS Integration Ecosystem
          </Typography>
          <Typography variant="body2" paragraph>
            UIP integrates with 15+ DHS and external systems to provide comprehensive traveler information:
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <Chip label="USCIS ELIS" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Immigration benefits</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="USCIS CIS" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Central Index System</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="ICE EID" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Enforcement database</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="CBP TECS" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Border crossing records</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="DHS IDENT" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Biometric repository</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="CBP ADIS" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Arrival/departure data</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="FBI TSDB" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Terrorist screening</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="State Dept CCD" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Visa/passport data</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="TSA Secure Flight" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Aviation security</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="USCG MISLE" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Maritime enforcement</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="FBI NCIC" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Criminal records</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="INTERPOL" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">International alerts</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Container>
  );
};

export default UIPSystemDetail;
